import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BarChart3, Eye, MousePointer, XCircle, Users, UserPlus, 
  TrendingUp, Download, ArrowLeft, Shield, AlertTriangle,
  Mail, Trash2, Clock, Check, Building2, MapPin, X
} from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * BusinessStats Page - LikeKind Business Dashboard
 * Per FINAL EXECUTION DIRECTIVE:
 * - Associated users (Personal Member seats) management
 * - Representation Responsibility notice (mandatory)
 * - Placards, opt-outs, feedback stats (presence only, no analytics)
 */
export default function BusinessStats() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'analytics'
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchBusinessData();
  }, [user, navigate]);

  const fetchBusinessData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch combined business stats and users
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/business/ad-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/business/stats-and-users`, { headers })
      ]);
      
      setStats(statsRes.data);
      setBusinessData(usersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Business account required');
        navigate('/');
      } else {
        toast.error('Failed to load business data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!confirm('Remove this user from your business account? They will lose their Personal Member benefits.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/business/associated-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User removed from business account');
      fetchBusinessData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const exportData = () => {
    if (!stats && !businessData) return;
    
    const csvContent = [
      ['Business Stats Export'],
      ['Generated', new Date().toISOString()],
      [''],
      ['Business Info'],
      ['Business Name', businessData?.business_info?.business_name || ''],
      ['Tier', businessData?.business_info?.tier_name || ''],
      ['Market', businessData?.business_info?.market || ''],
      [''],
      ['Seats'],
      ['Max Seats', businessData?.seats?.max_seats || 0],
      ['Used Seats', businessData?.seats?.used_seats || 0],
      ['Pending Invites', businessData?.seats?.pending_invites || 0],
      [''],
      ['Associated Users'],
      ['Name', 'Email', 'Status', 'Joined'],
      ...(businessData?.associated_users || []).map(u => [
        u.name, u.email, u.status, u.joined_at || 'Pending'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `likekind-business-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Data exported');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-terra)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <div className="sticky top-0 z-10" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 hover:opacity-70"
            style={{ color: 'var(--fy-text-secondary)' }}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
            Business Management
          </h1>
          <Button
            onClick={exportData}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            data-testid="export-button"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Business Info Card */}
        <Card className="p-5" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--lk-terra-muted)' }}>
              <Building2 className="w-7 h-7" style={{ color: 'var(--lk-terra)' }} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                {businessData?.business_info?.business_name || 'Your Business'}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--lk-sage-muted)', color: 'var(--lk-sage-dark)' }}>
                  {businessData?.business_info?.tier_name || 'Local Business'}
                </span>
                {businessData?.business_info?.verified && (
                  <span className="text-sm flex items-center gap-1" style={{ color: 'var(--lk-sage)' }}>
                    <Check className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              {businessData?.business_info?.market && (
                <p className="text-sm mt-2 flex items-center gap-1" style={{ color: 'var(--fy-text-secondary)' }}>
                  <MapPin className="w-3 h-3" />
                  {businessData.business_info.market}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Representation Responsibility Notice - MANDATORY */}
        <Card className="p-5" style={{ backgroundColor: 'var(--lk-terra-muted)', border: '1px solid var(--lk-terra)' }} data-testid="representation-notice">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-terra)' }} />
            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--lk-terra-dark)' }}>
                {businessData?.representation_notice?.title || 'Representation Responsibility'}
              </h3>
              <p className="text-sm whitespace-pre-line" style={{ color: 'var(--lk-terra-dark)', opacity: 0.9 }}>
                {businessData?.representation_notice?.content || 
                  'As the business account owner, you vouch for all associated users. Their actions on LikeKind reflect on your business.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Tab Selector */}
        <div className="flex gap-2 p-1 rounded-full" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-2.5 px-4 rounded-full font-medium text-sm transition-all flex items-center justify-center gap-2`}
            style={{
              backgroundColor: activeTab === 'users' ? 'var(--fy-surface-card)' : 'transparent',
              color: activeTab === 'users' ? 'var(--fy-text-primary)' : 'var(--fy-text-secondary)',
              boxShadow: activeTab === 'users' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4" />
            Associated Users
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex-1 py-2.5 px-4 rounded-full font-medium text-sm transition-all flex items-center justify-center gap-2`}
            style={{
              backgroundColor: activeTab === 'analytics' ? 'var(--fy-surface-card)' : 'transparent',
              color: activeTab === 'analytics' ? 'var(--fy-text-primary)' : 'var(--fy-text-secondary)',
              boxShadow: activeTab === 'analytics' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
            data-testid="tab-analytics"
          >
            <BarChart3 className="w-4 h-4" />
            Presence Stats
          </button>
        </div>

        {/* Associated Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Seats Summary */}
            <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Personal Member Seats</h3>
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                    Included with your {businessData?.business_info?.tier_name || 'business'} tier
                  </p>
                </div>
                <Button
                  onClick={() => setShowInviteModal(true)}
                  disabled={businessData?.seats?.available_seats <= 0}
                  className="flex items-center gap-2"
                  style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
                  data-testid="invite-user-button"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite User
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <p className="text-2xl font-bold" style={{ color: 'var(--lk-sage)' }}>
                    {businessData?.seats?.used_seats || 0}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Active</p>
                </div>
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <p className="text-2xl font-bold" style={{ color: 'var(--lk-terra)' }}>
                    {businessData?.seats?.pending_invites || 0}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Pending</p>
                </div>
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <p className="text-2xl font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                    {businessData?.seats?.max_seats || 2}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Total Seats</p>
                </div>
              </div>
            </Card>

            {/* Associated Users List */}
            <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Associated Users</h3>
              
              {(!businessData?.associated_users || businessData.associated_users.length === 0) ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
                  <p style={{ color: 'var(--fy-text-secondary)' }}>No associated users yet</p>
                  <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>
                    Invite team members to give them Personal Member benefits
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {businessData.associated_users.map((assocUser, idx) => (
                    <div 
                      key={assocUser.user_id || idx}
                      className="flex items-center gap-3 p-3 rounded-xl"
                      style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                      data-testid={`associated-user-${assocUser.user_id || idx}`}
                    >
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" 
                           style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
                        {assocUser.picture ? (
                          <img src={assocUser.picture} alt="" className="w-10 h-10 object-cover" />
                        ) : (
                          <span className="text-lg font-bold" style={{ color: 'var(--fy-text-secondary)' }}>
                            {assocUser.name?.charAt(0) || '?'}
                          </span>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>
                          {assocUser.name || 'Pending User'}
                          {assocUser.username && (
                            <span className="font-normal ml-1" style={{ color: 'var(--fy-text-secondary)' }}>
                              @{assocUser.username}
                            </span>
                          )}
                        </p>
                        <p className="text-sm truncate" style={{ color: 'var(--fy-text-secondary)' }}>
                          {assocUser.email}
                        </p>
                      </div>
                      
                      {/* Status */}
                      <div className="flex items-center gap-2">
                        {assocUser.status === 'pending' ? (
                          <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                                style={{ backgroundColor: 'var(--lk-terra-muted)', color: 'var(--lk-terra)' }}>
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                                style={{ backgroundColor: 'var(--lk-sage-muted)', color: 'var(--lk-sage-dark)' }}>
                            <Check className="w-3 h-3" />
                            Active
                          </span>
                        )}
                        
                        {/* Remove button */}
                        {assocUser.user_id && (
                          <button
                            onClick={() => handleRemoveUser(assocUser.user_id)}
                            className="p-2 rounded-lg hover:bg-red-100 transition-colors"
                            title="Remove user"
                            data-testid={`remove-user-${assocUser.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" style={{ color: 'var(--fy-accent-emphasis)' }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-4">
            {/* Key Metrics - Presence only, no click tracking */}
            <div className="grid grid-cols-2 gap-4">
              <MetricCard 
                icon={<Users className="w-5 h-5" />}
                label="Placard Placements"
                value={stats?.active_placements || 0}
                subValue="Static presence only"
              />
              <MetricCard 
                icon={<Eye className="w-5 h-5" />}
                label="Category Visibility"
                value={businessData?.business_info?.verified ? 'Active' : 'Pending'}
                subValue="No tracking metrics"
                isText
              />
            </div>

            {/* No Tracking Notice */}
            <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
                <div>
                  <h4 className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                    No Performance Tracking
                  </h4>
                  <p className="text-sm mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                    Per LikeKind&apos;s principles, business presence is about connection, not metrics. 
                    There are no impressions, clicks, or targeting analytics. Your placard provides 
                    static visibility in your category—nothing more.
                  </p>
                </div>
              </div>
            </Card>

            {/* Tips Section */}
            <Card className="p-5" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
                <TrendingUp className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
                Build Real Connections
              </h3>
              <ul className="text-sm space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
                <li>• Complete your business profile with quality images</li>
                <li>• Ensure your category accurately represents your business</li>
                <li>• Engage authentically—LikeKind is about community, not reach</li>
                <li>• Your associated users are your ambassadors—choose wisely</li>
              </ul>
            </Card>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteUserModal 
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            fetchBusinessData();
          }}
          availableSeats={businessData?.seats?.available_seats || 0}
        />
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, subValue, isText }) {
  return (
    <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
      <div className="mb-2" style={{ color: 'var(--lk-sage)' }}>
        {icon}
      </div>
      <p className={`${isText ? 'text-lg' : 'text-2xl'} font-bold`} style={{ color: 'var(--fy-text-primary)' }}>
        {value}
      </p>
      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{label}</p>
      {subValue && <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>{subValue}</p>}
    </Card>
  );
}

function InviteUserModal({ onClose, onSuccess, availableSeats }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/business/associated-users/invite`, {
        email: email.trim(),
        name: name.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User invited successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
            Invite Associated User
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:opacity-70">
            <X className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
          Invite a team member to receive Personal Member benefits through your business account.
          {availableSeats <= 0 && (
            <span className="block mt-2" style={{ color: 'var(--fy-accent-emphasis)' }}>
              You have no available seats. Remove an existing user first.
            </span>
          )}
        </p>

        <form onSubmit={handleInvite} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Email Address *
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="team@example.com"
              required
              disabled={availableSeats <= 0}
              data-testid="invite-email-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Name (optional)
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Team member's name"
              disabled={availableSeats <= 0}
              data-testid="invite-name-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || availableSeats <= 0}
              className="flex-1"
              style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
              data-testid="confirm-invite-button"
            >
              {loading ? 'Inviting...' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
